import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCustomerGroupMutations } from '~/hooks/use-customer-groups';
import { customerGroupSchema, EMPTY_CUSTOMER_GROUP, type CustomerGroupFormValues } from '~/lib/schemas/customer-group';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: CustomerGroupFormValues | null;
}

/** Add / edit a customer group (Ομάδα Πελατών). Membership criteria are edited separately. */
export function CustomerGroupEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useCustomerGroupMutations();
	const form = useForm<CustomerGroupFormValues>({
		resolver: zodResolver(customerGroupSchema) as Resolver<CustomerGroupFormValues>,
		defaultValues: initial ?? EMPTY_CUSTOMER_GROUP,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_CUSTOMER_GROUP);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('customers.groups.editTitle') : t('customers.groups.addTitle')}</DialogTitle>
					<DialogDescription>{t('customers.groups.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('customers.groups.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} autoFocus />
						</FormField>
						<FormField label={t('customers.groups.columns.name')} error={formState.errors.nm?.message}>
							<Input {...register('nm')} maxLength={50} aria-invalid={Boolean(formState.errors.nm)} />
						</FormField>
					</div>
					<FormField label={t('customers.groups.columns.description')} error={formState.errors.dsc?.message}>
						<Textarea {...register('dsc')} maxLength={500} rows={3} aria-invalid={Boolean(formState.errors.dsc)} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
